import React, { useState } from 'react';
import { TextField, Box, Typography, Radio, RadioGroup, FormControlLabel, FormLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import ProceedButton from '../../Components/Buttons/ProceedButton';
import { useAuth } from '../../Context/AuthContext';
import { FOODIMETRIC_HOST_URL } from '../../Utils/host';
import { useSpring, animated } from '@react-spring/web';
import { Helmet } from 'react-helmet-async';

// Custom styled TextField
const CustomTextField = styled(TextField)({
    '& label.Mui-focused': {
        color: '#3a8589',
    },
    '& .MuiInput-underline:after': {
        borderBottomColor: '#3a8589',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#3a8589',
        },
        '&:hover fieldset': {
            borderColor: '#3a8589',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#3a8589',
        },
    },
});

// Custom styled Radio
const CustomRadio = styled(Radio)({
    color: '#3a8589',
    '&.Mui-checked': {
        color: '#3a8589',
    },
});

const BMR = () => {
    const { user, logout } = useAuth();
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [age, setAge] = useState('');
    const [gender, setGender] = useState('male');
    const [bmr, setBmr] = useState(null);

    const handleWeightChange = (e) => setWeight(e.target.value);
    const handleHeightChange = (e) => setHeight(e.target.value);
    const handleAgeChange = (e) => setAge(e.target.value);
    const handleGenderChange = (e) => setGender(e.target.value);

    const calculateBMR = () => {
        // Use the Revised Harris-Benedict Equation
        let bmrValue;
        if (gender === 'male') {
            bmrValue = 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age);
        } else {
            bmrValue = 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age);
        }
        setBmr(bmrValue);
        return bmrValue;
    };

    const handleProceed = async () => {
        const computedBmr = calculateBMR();

        // Prepare calculation payload
        const calculationPayload = {
            user_id: user._id, // Replace `user._id` with the actual user ID from your app's context or state
            calculator_name: "BMR",
            parameters: {
                weight: `${weight} kg`,
                height: `${height} cm`,
                age: age,
                gender: gender,
            },
            result: `${computedBmr} calories/day`,
            calculation_details: "BMR calculated using the Revised Harris-Benedict Equation",
        };

        // Send the BMR calculation to the backend
        try {
            const response = await fetch(`${FOODIMETRIC_HOST_URL}/calculations`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,

                },
                body: JSON.stringify(calculationPayload),
            });

            if (response.status === 401) {
                logout()
                window.location.href = "/login";
                return; // Prevent further code execution
            }

            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }

            await response.json();
            // console.log("Calculation saved successfully:", data);
        } catch (error) {
            console.error("Error saving calculation:", error.message);
        }
    };

    // React Spring animation for fade and scale effect
    const animationProps = useSpring({
        opacity: bmr !== null ? 1 : 0,
        transform: bmr !== null ? 'scale(1)' : 'scale(0.9)',
        config: { tension: 200, friction: 20 }
    });

    return (
        <main className="py-8">
            <Helmet>
                <title>BMR Calculator - Estimate Your Basal Metabolic Rate | Foodimetric</title>
                <meta name="description" content="Calculate your Basal Metabolic Rate (BMR) to understand how many calories your body burns at rest. Use Foodimetric's AI-powered BMR calculator to tailor your diet and achieve your health goals with precision." />
                <meta name="keywords" content="BMR calculator, basal metabolic rate, calorie burn, daily calorie needs, weight management, nutrition tracking, diet planning, Foodimetric BMR, metabolism calculator, Nigerian health tools" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="Calculate Your BMR | Foodimetric" />
                <meta name="twitter:description" content="Quickly estimate your Basal Metabolic Rate (BMR) using the Revised Harris-Benedict Equation." />

                <script type="application/ld+json">
                    {`
{
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Foodimetric BMR Calculator",
  "url": "https://www.foodimetric.com/anthro/BMR",
  "applicationCategory": "HealthApplication",
  "operatingSystem": "All",
  "description": "Calculate your Basal Metabolic Rate (BMR) using the Revised Harris-Benedict Equation: For men: BMR = 88.362 + (13.397 × weight) + (4.799 × height) - (5.677 × age); For women: BMR = 447.593 + (9.247 × weight) + (3.098 × height) - (4.330 × age)."
  "creator": {
    "@type": "Organization",
    "name": "Foodimetric"
  }
}
`}
                </script>

            </Helmet>
            <div className="bg-white p-8 min-h-screen">
                <Box sx={{
                    maxWidth: { xs: '100%' },
                    mx: 'auto',
                    px: { xs: 2, sm: 4 },
                    py: 4,
                    textAlign: 'center',
                    fontFamily: 'Outfit, serif',
                }}>
                    <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                        What is Basal Metabolic Rate (BMR)?
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', color: '#444', fontFamily: 'Outfit, serif' }}>
                        Your Basal Metabolic Rate (BMR) is the number of calories your body needs to maintain basic life functions while at rest—such as breathing, circulation, and cell production.
                    </Typography>
                    <CustomTextField
                        label="Weight (kg)"
                        type="number"
                        value={weight}
                        onChange={handleWeightChange}
                        fullWidth
                        margin="normal"
                        sx={{ fontFamily: 'Outfit, serif' }}
                    />
                    <CustomTextField
                        label="Height (cm)"
                        type="number"
                        value={height}
                        onChange={handleHeightChange}
                        fullWidth
                        margin="normal"
                        sx={{ fontFamily: 'Outfit, serif' }}
                    />
                    <CustomTextField
                        label="Age (years)"
                        type="number"
                        value={age}
                        onChange={handleAgeChange}
                        fullWidth
                        margin="normal"
                        sx={{ fontFamily: 'Outfit, serif' }}
                    />
                    <Box sx={{ textAlign: 'left', mt: 2 }}>
                        <FormLabel component="legend" sx={{ fontFamily: 'Outfit, serif' }}>Gender</FormLabel>
                        <RadioGroup
                            row
                            value={gender}
                            onChange={handleGenderChange}
                            aria-label="gender"
                            name="gender"
                        >
                            <FormControlLabel value="male" control={<CustomRadio />} label="Male" sx={{ fontFamily: 'Outfit, serif' }} />
                            <FormControlLabel value="female" control={<CustomRadio />} label="Female" sx={{ fontFamily: 'Outfit, serif' }} />
                        </RadioGroup>
                    </Box>
                    <ProceedButton color="#ffba08" type="button" auth="authorized" onClick={handleProceed} />
                    {/* Animated BMR result */}
                    {bmr !== null && (
                        <animated.div style={animationProps}>
                            <animated.div style={animationProps} aria-live="polite">
                                <Typography variant="h6" sx={{ mt: 4 }}>
                                    Your BMR is: {bmr.toFixed(2)} calories/day
                                </Typography>
                            </animated.div>

                            <Typography sx={{ mt: 2, fontSize: '1rem', color: 'gray', fontFamily: 'Outfit, serif' }}>
                                BMR Formula Used:
                            </Typography>
                            <Typography sx={{ mt: 1, fontStyle: 'italic', color: '#555', fontFamily: 'Outfit, serif' }}>
                                Revised Harris-Benedict Equation
                            </Typography>
                        </animated.div>
                    )}
                    <div className='text-left'>
                        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            How to Use This BMR Calculator
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', color: '#444', fontFamily: 'Outfit, serif' }}>
                            To estimate your Basal Metabolic Rate (BMR), enter your weight in kilograms, height in centimeters, age in years, and select your gender.
                            Then, click on the {' '} <span style={{ color: '#ffba08', fontWeight: 'bold' }}>Proceed</span> button to calculate how many calories your body burns at rest each day.
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 4, mb: 1, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            Why is BMR important?
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', color: '#444', fontFamily: 'Outfit, serif' }}>
                            Knowing your BMR helps tailor your diet and exercise routine. If you’re trying to lose weight, knowing how many calories your body burns naturally can help you create a proper calorie deficit.
                        </Typography>

                        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            Frequently Asked Questions
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            1. Is BMR the same as daily calorie needs?
                        </Typography>
                        <Typography sx={{ fontFamily: 'Outfit, serif' }}>
                            No. BMR only calculates the calories needed for basic body functions. To determine total daily calorie needs, you need to factor in your activity level.
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            2. Can I use this to plan my diet?
                        </Typography>
                        <Typography sx={{ fontFamily: 'Outfit, serif' }}>
                            Absolutely. BMR is the foundation for any diet plan. Once you know your BMR, multiply it by an activity factor to estimate your daily energy expenditure.
                        </Typography>
                    </div>
                </Box>
            </div>
        </main>
    );
};

export default BMR;
